/**
 * Shared contract type + overlap check for highlighting the parts of a
 * committed OIO draft that changed in the most recent rewrite.
 *
 * `changed_ranges` is produced by the rewrite backend's persist_commit step
 * and delivered through rewrite STATE_SNAPSHOT updates. Ranges are 1-indexed,
 * inclusive line numbers into the markdown that becomes the new draft
 * content. Deletions are intentionally NOT represented — only "insert" and
 * "replace" ranges ever appear.
 */
export interface ChangedRange {
  start_line: number
  end_line: number
  type: "insert" | "replace"
}

/** Minimal shape of the remark/hast `position` attached to every markdown AST node. */
export interface NodePosition {
  start?: { line?: number }
  end?: { line?: number }
}

/**
 * A markdown AST node is "changed" if its own line range overlaps any
 * changed range: start <= range.end_line && end >= range.start_line.
 */
export function isNodeChanged(
  position: NodePosition | undefined | null,
  changedRanges: ChangedRange[] | undefined | null,
): boolean {
  const startLine = position?.start?.line
  const endLine = position?.end?.line
  if (startLine == null || endLine == null || !changedRanges?.length) return false
  return changedRanges.some((range) => startLine <= range.end_line && endLine >= range.start_line)
}

/**
 * Semantic class for a "recently changed" block. The visual treatment lives
 * in na-shell.css (`.diff-highlight`) on the shell's paper/gold tokens, and
 * is neutralized wholesale when the container carries `.diff-nav-dismissed`
 * — dismissing must not re-render the (expensive) markdown tree.
 */
export const DIFF_HIGHLIGHT_CLASS = "diff-highlight"
